import { AfterViewInit, Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MosDevice } from '@common/data-models/service/mos-device';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { AppTheme } from '@src/suite/app.theme';
import { DRAGTYPES } from '@src/suite/common/enums/enums';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { SidecarEfffectActions } from '@src/suite/common/store/fullscreen';
import { selectSidecarSelectedMosPlugin } from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { selectMosItems } from '@src/suite/common/store/mos/selectors/mos.selectors';
import { fireCustomEvent, getElementFromCoordinates } from '@src/suite/common/utils/misc-utils';
import { getMosCommand } from '@src/suite/common/utils/mos-utlis';
import { Subject, takeUntil } from 'rxjs';

import { ComboBoxComponent } from '../../controls/combo-box/combo-box';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { UnsafePipe } from '../../pipes/unsafe';

const defaultSettingsPrefix = 'URL=';

@Component({
    selector: 'st-mos-full-screen',
    imports: [ClickStopPropagationDirective, ComboBoxComponent, UnsafePipe],
    templateUrl: './st-mos-full-screen.component.html',
    styleUrl: './st-mos-full-screen.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-mos-full-screen',
    },
})
export class StMosFullScreenComponent implements OnDestroy, AfterViewInit {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    mosEventService = inject(MosEventService);
    appTheme = inject(AppTheme);

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    mosItemList = this.store.selectSignal(selectMosItems);
    readonly mosDevice = computed(() => {
        const mosDevice = this.store.selectSignal(selectSidecarSelectedMosPlugin(this.sidecarMenu()))();
        return mosDevice as MosDevice;
    });

    readonly mosDeviceValue = computed(() => {
        if (!this.mosDevice()) {
            return { id: '', title: '' };
        }
        return { id: this.mosDevice().ID, title: this.mosDevice().Description };
    });

    destroyed$ = new Subject<void>();

    readonly mosIframe = viewChild<ElementRef<HTMLIFrameElement>>('mosIframe');
    readonly mosIframeOverlay = viewChild<ElementRef<HTMLIFrameElement>>('mosIframeOverlay');

    private readonly origin = signal<string>('');
    public readonly mosUrl = signal<string>('');

    private readonly sameOrigin = computed(() => {
        const mosUrl = new URL(this.mosUrl(), window.location.href);
        const currentUrl = window.location;
        return mosUrl.origin === currentUrl.origin;
    });

    constructor() {
        this.mosEventService
            .onMosClientCommand()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(mos => {
                if (this.mosIframe()?.nativeElement?.contentWindow && mos.sidecar === this.sidecarMenu() && mos.mosPluginId === this.mosDevice()?.ID) {
                    if (mos?.command?.length > 0 && mos?.command.includes('<mos>')) {
                        this.mosIframe().nativeElement.contentWindow.postMessage(mos.command, '*');
                    }
                }
            });

        effect(() => {
            this.setMosUrl(this.mosDevice());
        });

        effect(() => {
            if (this.mosIframe()?.nativeElement.contentWindow) {
                const darkModeAttribute = this.appTheme.darkMode() ? 'dark' : 'light';
                this.mosIframe().nativeElement.contentWindow.postMessage({ name: 'dark-mode', value: darkModeAttribute }, '*');
            }
        });

        effect(() => {
            if (!this.mosDevice()) {
                this.store.dispatch(SidecarEfffectActions.setCurrentMosPlugin({ mosPluginId: this.mosItemList()?.[0]?.id ?? null, sidecarMenu: this.sidecarMenu() }));
            }
        });
    }

    private mosHandler;

    ngAfterViewInit() {
        this.mosHandler = this.mosMsgFromPlugin.bind(this);
        window.addEventListener('message', this.mosHandler, false);
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.store.dispatch(SidecarEfffectActions.resetMosState({ sidecar: this.sidecarMenu() }));
        window.removeEventListener('message', this.mosHandler, false);
    }

    onMosChange(event: any) {
        this.store.dispatch(SidecarEfffectActions.setCurrentMosPlugin({ mosPluginId: event.id, sidecarMenu: this.sidecarMenu() }));
    }

    setMosUrl(mosDevice: MosDevice) {
        window.removeEventListener('message', this.mosHandler, false);
        if (mosDevice) {
            this.origin.set(`${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${window.location.hash ? window.location.hash : ''}`);
            const localMosUrl = (mosDevice.DefaultSettings?.startsWith(defaultSettingsPrefix) ? mosDevice.DefaultSettings?.substring(defaultSettingsPrefix.length) : mosDevice.DefaultSettings) || '';
            this.mosUrl.set(localMosUrl.indexOf('?') === -1 ? `${localMosUrl}?origin=${this.origin()}` : `${localMosUrl}&origin=${this.origin()}`);
            window.addEventListener('message', this.mosHandler, false);
        } else {
            this.mosUrl.set('');
        }
    }

    mosMsgFromPlugin(event: MessageEvent) {
        if (!this.sameOrigin()) {
            // Handle drag & drop events from window.postMessage
            if (event?.data?.type === DRAGTYPES.MosPluginDragStart) {
                if (this.mosIframeOverlay()?.nativeElement) {
                    this.mosIframeOverlay().nativeElement.classList.add('st-mos-full-screen-iframe-overlay-blur');
                }
                return;
            }
            if (event?.data?.type === DRAGTYPES.MosPluginDragEnd) {
                if (this.mosIframeOverlay()?.nativeElement) {
                    this.mosIframeOverlay().nativeElement.classList.remove('st-mos-full-screen-iframe-overlay-blur');
                }
                if (this.mosIframe()?.nativeElement) {
                    const rect = this.mosIframe().nativeElement.getBoundingClientRect();
                    const coord = {
                        x: rect.x + event.data.coord.pageX,
                        y: rect.y + event.data.coord.pageY,
                    };
                    const target = getElementFromCoordinates(coord.x, coord.y);
                    if (target) {
                        fireCustomEvent('drop', target, event, coord.x, coord.y);
                    }
                }

                return;
            }
        }

        if (event?.data?.length > 0) {
            // Handle the Message
            // To Reply, issue a postMessage on the event source.
            const mosCommandType = getMosCommand(event.data);
            this.store.dispatch(MosActions.mosServerCommandSend({ command: event.data, mosPluginId: this.mosDevice()?.ID, sidecar: this.sidecarMenu(), mosCommandType }));
        }
    }

    onMosLoad() {
        if (this.mosIframe()?.nativeElement.contentWindow) {
            const darkModeAttribute = this.appTheme.darkMode() ? 'dark' : 'light';
            this.mosIframe().nativeElement.contentWindow.postMessage({ name: 'dark-mode', value: darkModeAttribute }, '*');
        }
    }
}
